import { useEffect, useState } from "react";
import { api, qs } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { AzzeraFiltri, FilterBar, inputCls, useFiltersMeta } from "@/components/Filters";
import { num } from "@/lib/format";

const TITOLI = {
  numero_pratica: "Nr. pratica", data_caricamento: "Data caricamento", data_ingresso: "Data ingresso",
  data_approvazione: "Data approvazione", data_liquidazione: "Data liquidazione",
  dealer_codice: "Codice dealer", dealer_nome: "Dealer", commerciale_slug: "Commerciale",
  gestione_caricamento: "Operatrice", importo_finanziato: "Importo finanziato",
  codice_dealer: "Codice dealer", ragione_sociale: "Ragione sociale", tipo_premio: "Tipo premio",
  importo_premio: "Importo premio",
};
const titolo = (c) => {
  if (TITOLI[c]) return TITOLI[c];
  const t = c.replace(/_/g, " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
};
const valore = (v) => (v === null || v === undefined || v === ""
  ? "—" : typeof v === "boolean" ? (v ? "SI" : "NO") : typeof v === "number" ? num(v, 2) : String(v));

/** Archivi consultabili ed esportabili in CSV. */
export default function Archivio() {
  const meta = useFiltersMeta();
  const [archivi, setArchivi] = useState([]);
  const [tipo, setTipo] = useState("caricate");
  const [filters, setFilters] = useState({ anno: 2026 });
  const [dealers, setDealers] = useState([]);
  const [search, setSearch] = useState("");
  const [d, setD] = useState(null);
  const [reset, setReset] = useState(0);

  useEffect(() => {
    api.get("/archivio").then((r) => setArchivi(r.data.archivi || [])).catch(() => setArchivi([]));
  }, []);

  useEffect(() => {
    setD(null);
    api.get(`/archivio/${tipo}?${qs({ ...filters, search, limite: 500 })}`)
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [tipo, filters, search]);

  useEffect(() => {
    api.get(`/dashboard/dealers-periodo?${qs({ anno: filters.anno, commerciale: filters.commerciale })}`)
      .then((r) => setDealers(r.data.dealers || [])).catch(() => setDealers([]));
  }, [filters.anno, filters.commerciale]);

  return (
    <div data-testid="archivio-page">
      <SectionTitle sub="In caso di doppioni del numero pratica vale sempre la versione aggiornata più di recente.">
        Archivio
      </SectionTitle>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="archivio-tabs">
        {archivi.map((a) => (
          <button key={a.key} onClick={() => setTipo(a.key)} data-testid={`archivio-tab-${a.key}`}
            className={`px-4 py-2 rounded-md text-sm transition-colors duration-200 border
              ${tipo === a.key ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {a.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <FilterBar key={reset} meta={meta} value={filters} onChange={setFilters} dealers={dealers}
          fields={["anno", "mese", "commerciale", "settore", "dealer"]} />
        <input className={`${inputCls} min-w-[240px] mb-6`} placeholder="Cerca pratica, dealer, cliente…"
          value={search} onChange={(e) => setSearch(e.target.value)} data-testid="archivio-search" />
        <AzzeraFiltri onClick={() => { setFilters({ anno: 2026 }); setSearch(""); setReset((v) => v + 1); }} />
        <a href={`/api/archivio/${tipo}.csv?${qs({ ...filters, search })}`} data-testid="archivio-csv"
          className="mb-6 inline-flex items-center rounded-md bg-emerald-600 px-4 py-2 text-sm
            font-semibold text-white hover:bg-emerald-700 transition-colors duration-200">
          Scarica CSV
        </a>
        <a href={`/api/export/premi.xlsx?${qs({ anno: filters.anno, commerciale: filters.commerciale })}`} data-testid="archivio-premi-xlsx"
          title="Premi assicurativi inseriti dal form gestione pratica (solo colonne compilate), filtrabili per commerciale"
          className="mb-6 inline-flex items-center rounded-md border border-sky-600 px-4 py-2 text-sm
            font-semibold text-sky-700 hover:bg-sky-50 transition-colors duration-200">
          Esporta premi da form (Excel)
        </a>
      </div>

      {!d ? (
        <div className="text-sm text-slate-500" data-testid="archivio-loading">Caricamento…</div>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100">
            {d.label} · {num(d.totale)} righe mostrate (max 500 · il CSV contiene tutte le righe)
          </div>
          <div className="overflow-auto max-h-[640px]">
            <table className="w-full text-xs" data-testid="archivio-table">
              <thead className="sticky top-0 bg-slate-50 text-slate-500 uppercase text-[9px] tracking-wider">
                <tr>
                  {d.colonne.map((c) => (
                    <th key={c} className="text-left px-3 py-2 whitespace-nowrap">{titolo(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r, i) => (
                  <tr key={`${r.numero_pratica || r.codice_dealer || i}-${i}`}
                    className={`border-t border-slate-100 ${i % 2 ? "bg-slate-100/80" : "bg-white"}`}>
                    {d.colonne.map((c) => (
                      <td key={c} className="px-3 py-1.5 whitespace-nowrap font-mono text-slate-600">
                        {valore(r[c])}
                      </td>
                    ))}
                  </tr>
                ))}
                {!d.rows.length && (
                  <tr><td colSpan={d.colonne.length} className="px-4 py-6 text-slate-400">
                    Nessuna riga per i filtri selezionati.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
